import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { scrimIn } from '../design/motion';
import {
  WizardStepContext,
  type WizardModalName,
  type WizardStepValue,
} from '../design/wizardStepContext';
import type { UploadOverlayVm } from '../useUploadOverlay';
import { UploadDescribePage } from './UploadDescribePage';
import { UploadDetailPage } from './UploadDetailPage';
import { UploadReviewPage } from './UploadReviewPage';
import { UploadPublishPage } from './UploadPublishPage';

/**
 * UploadWizard
 *
 * The overlay shell shared by all five upload steps (`/upload/1-select` …
 * `/upload/5-publish`), which are nested as child routes under this one in
 * App.tsx. Its whole reason to exist is the transition the brief asks for:
 * stepping between steps must swap ONLY the modal box, while the page chrome and
 * the scrim behind it stay mounted and still.
 *
 * WHERE THE BACKGROUND LIVES NOW. This wizard used to render its own `<PageLayout>`
 * + tool-card grid as the profile screen behind the scrim. It no longer does —
 * that background is rendered ONCE by `ProfileScreen`, the layout route that BOTH
 * `/profile-full` and this wizard are children of. The wizard mounts into that
 * layout's `overlay` slot (an `<Outlet/>`), so the profile chrome stays mounted
 * and still whether the wizard is open or not. This is what stops the profile
 * page from replaying its entrance ("refreshing") when the wizard closes: closing
 * the wizard removes only this overlay, never the background. See ProfileScreen.
 *
 * WHAT PERSISTS (rendered once here, NOT per step):
 *   - The `fixed inset-0` overlay root, portalled to `document.body`. This is the
 *     one legitimate `fixed` in the build — an overlay is viewport-pinned by
 *     definition, not by a canvas coordinate. It portals for the reason ModalShell
 *     used to: `fixed` paints as an overlay but DOM position kept the old modal a
 *     descendant of the content it covered, leaving the page chrome reachable by
 *     keyboard. On body it has real siblings to mark inert.
 *   - The scrim — a full-viewport `bg-scrim` that covers the whole 1440x1024
 *     canvas including the header. It fades in on wizard ENTER and out on wizard
 *     LEAVE only (top-level AnimatePresence in App.tsx), never per step, so the
 *     backdrop is steady while the contents swap.
 *   - The `top-header` centring box. The modal is centred in the area BELOW the
 *     header, expressed as a top offset of --header-height — a structural relation
 *     between two components, not a canvas coordinate. `top-header` not `pt-header`:
 *     padding-top inside a full-viewport box plus the gutter's 31.5px bottom pad
 *     pulled the centre up to y=208.8; offsetting the box's top edge lands it at
 *     224 against the design's 231, the residual 7px left unreproduced.
 *   - THE MODAL FRAME ITSELF. The measured 1056x640 rounded, stroked box is now a
 *     single element rendered ONCE here — it is the `role="dialog"`, and it never
 *     remounts, fades or moves between steps. This is the whole point: the user
 *     wants the box to STAY on screen and only its contents to change. The box
 *     classes moved up from ModalShell verbatim (see the frame in the render).
 *   - ONE focus trap (inert siblings + Tab cycle) on that stable frame. Because
 *     the frame is persistent, the trap runs once for the wizard's lifetime rather
 *     than re-attaching per step. A separate effect handles Escape + focusing the
 *     frame on each step (keyed on pathname).
 *
 * WHAT SWAPS (per step): only the CONTENTS. Each step page returns a `<ModalShell>`
 * that is now just the header/body/footer wrapped in an animated `motion.div`
 * (stepFadeIn, opacity only). The `<Outlet/>` — wrapped in `<AnimatePresence
 * mode="wait">` keyed on the pathname — sits INSIDE the persistent frame, so one
 * step's content fades out before the next fades in, in place, while the box holds
 * still around it. No travel and no direction: the box does not move, so any
 * transform would be motion with nowhere to go (see stepFadeIn).
 *
 * DISMISS. Three ways out, all to /profile-full — the same destination as the
 * frames' own "Cancel"/"Finish", so every dismiss matches the drawn one rather
 * than inventing a new target:
 *   - Clicking the scrim / the free space around the modal (onBackdropClick, guarded
 *     so a click INSIDE the box never closes it).
 *   - Escape (wired in the per-step effect, at the user's explicit request).
 * The design draws no close affordance, so both are requested interactions beyond
 * the static frames, documented as such (see ModalShell's onDismiss note).
 *
 * ACCESSIBLE NAME. The frame owns `role="dialog"` but the name is per-step (P1
 * points at its visible title via `labelledBy`; P2–P5 use `label`). The mounted
 * ModalShell reports its name up through context (`useWizardModalName`) in an
 * effect; the frame renders `aria-labelledby`/`aria-label` from it. An effect is
 * safe because the name is not visually rendered and mode="wait" keeps one step
 * mounted at a time.
 */

/**
 * `WizardModalName` / `WizardStepContext` / `useWizardModalName` are declared in the
 * Design's copy of THIS file, and `ModalShell` imports them from here. In the backend
 * they live in `../design/wizardStepContext` instead — imported above, and provided
 * below exactly as the Design provides them. The move is forced: `OverlayFrame` also
 * provides this context (it is still FlowPickerReskin's shell), and two separate
 * `createContext` calls would leave one of the two frames with a permanently unnamed
 * dialog. See that module's header.
 */

/**
 * WHAT CHANGED FROM THE DESIGN'S COPY OF THIS FILE, and nothing else did:
 *
 *   1. `<Outlet/>` -> a switch on the Vm. The Design routes its five steps as five
 *      nested routes and swaps them with an Outlet; the backend wizard keeps its step
 *      in local state because it holds state a URL cannot — `onResume`/`onStartNew`
 *      pick a landing stage at runtime, `pendingDuplicates` is an interstitial that is
 *      not a step, and `onNextFromDetails` refuses to advance while `detailErrors` is
 *      non-empty. Real routes would need a guard on every path to re-derive what the
 *      `stage` union already encodes.
 *   2. `navigate('/profile-full')` (click-off, Escape) -> `vm.props.onClose()`, the
 *      backend's own dismiss. Same gesture, same intent, no router.
 *   3. The per-step effect keys on `stepKey` instead of `location.pathname` — the same
 *      "re-run when the step changes" signal, expressed in local state.
 *
 * The portal, the scrim, the inert sweep, the Tab cycle, the centring box, the
 * persistent frame and every className are the Design's, untouched. The portal in
 * particular is KEPT deliberately: it is what lifts the overlay out of `.tb-tools`,
 * whose V1 element rules would otherwise still match these buttons (see
 * tools/shared.tsx).
 *
 * FIVE DESIGN PAGES, THREE BACKEND STAGES. The Design draws P1..P5; the backend's
 * `stage` union is `details | instructions | review` and its `phase` carries the rest.
 * The mapping is P2/P3/P4 -> the three stages, P5 -> `phase.kind === 'done'`.
 *
 * P1 IS NOT RENDERED HERE, but it IS inside the overlay. The design shows the automation
 * picker as the first frame in this same box, and it is — see `UploadOverlayPages.tsx`,
 * which renders P1 in an identical frame while no automation is chosen and mounts this
 * wizard once one is. It cannot be a case in the switch below because `useUploadOverlay`
 * requires a flow to exist before it can be called at all, and this component's whole
 * input is that hook's return value. `onBackToSelect` is how the two halves join up:
 * P2's Back goes to P1, the previous FRAME, rather than closing the overlay.
 */
export function UploadWizard({
  vm,
  onBackToSelect,
  renderScrim = true,
}: {
  vm: UploadOverlayVm;
  /** Return to P1 (the automation picker) — the frame before this one. */
  onBackToSelect: () => void;
  /**
   * Whether this frame paints its own scrim. Defaults to TRUE, which is the
   * designer's original behaviour and keeps this file correct on its own.
   *
   * `UploadOverlayPages` passes FALSE, because it mounts ONE scrim that outlives
   * the P1 -> P2 handoff. Both halves drawing their own meant that, for a single
   * painted frame during the swap, neither existed — the overlay blinked out to
   * the bright page and the new scrim faded up from zero. Visible in Chrome,
   * coalesced away by other engines. See the persistent-scrim note there.
   *
   * A PROP rather than a deletion: this file is a verbatim copy of the designer's
   * own, so the change is additive and opt-in, and the default path is untouched.
   */
  renderScrim?: boolean;
}) {
  const { onClose } = vm.props;

  // The step identity, standing in for the Design's `location.pathname`: it changes
  // exactly when the visible step does, which is what the per-step effect and
  // <AnimatePresence> both key on.
  const stepKey = vm.phase.kind === 'done' ? 'done' : vm.stage;

  // The PERSISTENT frame — the one `role="dialog"` element, rendered once and
  // never remounted between steps. The focus trap targets it. A ref (not state)
  // because it is stable: it mounts with the wizard and lives until the wizard
  // closes, so the trap does not need to re-run when it changes (it does not).
  const dialogRef = useRef<HTMLDivElement>(null);

  // The overlay root, so the inert sweep can find the node whose siblings on
  // <body> should be disabled.
  const overlayRef = useRef<HTMLDivElement>(null);

  // The current step's accessible name, reported up by its ModalShell and rendered
  // on the persistent frame. State so the frame re-renders its aria-* when the step
  // changes. Kept minimal so a re-render only fires when a name field actually
  // differs (ModalShell sets it in an effect on mount / name change).
  const [modalName, setModalName] = useState<WizardModalName>({});

  // The value handed to the mounted step: just the name setter now. The dialog
  // element is no longer registered per step (the frame is persistent). Memoised
  // so consumers only re-render if the callback identity changes — it never does.
  const stepValue = useMemo<WizardStepValue>(() => ({ setModalName }), [setModalName]);

  // Click-off dismiss. Fires ONLY when the click landed on the backdrop element
  // the handler is attached to (e.target === e.currentTarget) — a click inside
  // the modal box bubbles up with target = some inner element, so it is ignored
  // and never closes the wizard. Divergence 2: the Design navigated to
  // /profile-full here; the backend closes the overlay in place, which is the
  // same gesture with the same intent (Share.tsx keeps the draft on close).
  const onBackdropClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) onClose();
    },
    [onClose],
  );

  // ONE focus trap for the whole overlay, on the STABLE persistent frame. Runs
  // once when the wizard mounts (frame + overlay both present) and cleans up when
  // it unmounts. This is the logic that used to live in ModalShell, hoisted so it
  // runs once across steps rather than re-attaching on every box swap.
  //
  // ESCAPE and re-focus per step depend on `location.pathname`, so they live in a
  // SEPARATE effect below — the trap itself must NOT re-run per step or it would
  // restore focus to the previous element mid-wizard.
  useEffect(() => {
    const dialog = dialogRef.current;
    const overlay = overlayRef.current;
    if (!dialog || !overlay) return;

    // Mark every sibling of the overlay inert, so the page chrome behind the
    // scrim leaves the tab order and the accessibility tree together. `inert` is
    // the platform primitive for exactly this and needs no aria bookkeeping.
    const siblings = overlay.parentElement
      ? ([...overlay.parentElement.children] as HTMLElement[]).filter(
          // A caller-owned scrim (renderScrim={false}) is a body-sibling of this
          // overlay, and `inert` would kill its clicks as well as its focusability,
          // silently disabling backdrop dismiss. It holds nothing focusable, so
          // skipping it costs the trap nothing. Harmless when we draw our own.
          (el) => el !== overlay && !el.hasAttribute('data-overlay-scrim'),
        )
      : [];
    const restore = siblings.map((el) => [el, el.inert] as const);
    siblings.forEach((el) => {
      el.inert = true;
    });

    // Remember what had focus before the wizard opened, so it can be restored when
    // the wizard closes. Captured ONCE here (not per step) — the initial focus-in
    // is done by the per-step effect below so it also fires when stepping.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Cycle Tab within the dialog. Queried on each keypress rather than cached,
    // because the footer's controls are passed in as children and a page may
    // render them conditionally.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const focusable = [
        ...dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      // The backend's tsconfig sets `noUncheckedIndexedAccess`, which the Design's
      // does not, so an index read is `T | undefined` here and the two focus calls
      // below would not compile. The length check above already proves both are
      // present; this is the type system catching up, not a behaviour change.
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;

      // Wrapping is explicit at both ends, and the `!dialog.contains` case covers
      // focus starting on the dialog element itself.
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restore.forEach(([el, wasInert]) => {
        el.inert = wasInert;
      });
      previouslyFocused?.focus?.();
    };
    // Runs ONCE for the wizard's lifetime: the frame is persistent, so there is no
    // per-step node to re-target. Escape + re-focus-per-step are the next effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ESCAPE to dismiss + move focus into the frame on each step. Separate from the
  // trap so it re-runs per step (keyed on `stepKey` — divergence 3, the same
  // "the visible step changed" signal the Design read off location.pathname)
  // without tearing down the inert sweep. Escape closes the overlay — the same
  // destination as click-off and the frames' Cancel/Finish — at the user's
  // explicit request; the design draws no close affordance, so this is a
  // requested interaction, not one read off the frame (see ModalShell's
  // onDismiss note).
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Focus the frame itself (not the first control) so a screen reader announces
    // the dialog's name before the button labels — on first open and each step.
    dialog.focus();

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [stepKey, onClose]);

  return (
    <>
      {/* No background here any more: ProfileScreen (the layout route this wizard
          is a child of) renders the profile chrome + card grid once and keeps it
          mounted, so it does not flash or replay its entrance when the wizard opens
          or closes. This component is purely the overlay on top of it. */}

      {/* The overlay root, portalled to body — see the header. `data-overlay-root`
          is what the inert sweep uses to find the node whose body-siblings to
          disable. `fixed inset-0` is viewport-pinned overlay behaviour, not a
          canvas coordinate. */}
      {createPortal(
        <div
          ref={overlayRef}
          // `pointer-events-none` only when the scrim is someone else's: this layer is
          // then transparent, and must not swallow clicks meant for the scrim beneath
          // it. The centring box re-enables them for itself either way, so its own
          // guarded click-off is unaffected in both modes.
          className={['fixed inset-0 z-50', renderScrim ? '' : 'pointer-events-none'].join(' ')}
          data-overlay-root
        >
          {/* The scrim covers the FULL viewport including the header. Rendered
              once here: it fades in on wizard enter and out on wizard leave only
              (App.tsx's top-level AnimatePresence), never per step.

              CLICK-OFF: the scrim is the backdrop, so clicking it dismisses the
              wizard (to /profile-full, matching Cancel/Finish — see header). It
              sits BEHIND the centring box, so a click only reaches the scrim in
              the header strip above the centring box; the region beside and
              around the modal is handled by the centring box's own guarded
              handler below. Both go through onBackdropClick. Not aria-hidden any
              more, since it is now an interactive dismiss target.

              SKIPPED when `renderScrim` is false — see that prop. The caller then
              owns a longer-lived scrim, and drawing a second one here would be the
              very thing that made P1 -> P2 flash. */}
          {renderScrim ? (
            <motion.div
              variants={scrimIn}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={onBackdropClick}
              className="absolute inset-0 bg-scrim"
            />
          ) : null}

          {/* The centring box: starts BELOW the header (top-header), see header.
              It also dismisses on a backdrop click — onBackdropClick fires only
              when the click LANDS ON this box itself (its padding / the flex free
              space around the modal), not when it bubbles up from the modal, so
              clicking inside the modal never closes it. */}
          {/* The backdrop's click-to-dismiss is a pointer-only CONVENIENCE. Keyboard users dismiss
              with Escape, which is wired on the document (see the per-step effect / header notes), so
              the dismissal is fully keyboard-operable without this element itself being focusable.
              A full-screen backdrop must NOT be a tab stop (role="button" + tabIndex here would wrap
              the entire dialog in a focusable control and confuse screen-reader traversal), so the
              key-events rule is disabled for this line specifically rather than satisfied with fake
              interactivity. */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            onClick={onBackdropClick}
            className="pointer-events-auto absolute inset-x-0 bottom-0 top-header flex items-center justify-center p-modal-x"
          >
            {/* THE PERSISTENT FRAME. This is the modal box itself — the measured
                1056x640 rounded box with the 1px stroke — and it is rendered ONCE
                and NEVER keyed, so it does not remount, fade or move when you step
                from 1→2→…→5. It is the `role="dialog"` element and the focus-trap
                target (a stable node, hence a plain ref). Its accessible name is
                per-step, reported up by the mounted ModalShell via context and
                rendered here.

                The box classes are the ones that used to live on ModalShell's
                animated div, moved up here VERBATIM: `max-w-modal` + `h-modal`
                (the measured width/height), `max-h-full` to shrink inside the
                padded overlay, `rounded-lg border border-ink bg-surface`. */}
            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={modalName.labelledBy}
              aria-label={modalName.labelledBy ? undefined : modalName.label}
              // -1 so the frame can take focus programmatically on open / each step
              // without joining the tab order as a stop of its own.
              tabIndex={-1}
              className={[
                'relative flex w-full max-w-modal flex-col',
                'h-modal max-h-full',
                'rounded-lg border border-ink bg-surface',
              ].join(' ')}
            >
              {/* Only the CONTENTS swap here. mode="wait" plays one step's
                  fade-out before the next fades in — sequential, so the two never
                  render as side-by-side flex siblings. The transition is a PURE
                  opacity cross-dissolve in place (stepFadeIn on ModalShell's
                  content root): no travel, because the frame above does not move —
                  same box, same position, only the header/body/footer inside it
                  change. This is the fix for "the page disappears off screen": the
                  box no longer animates at all; the content does. Keyed on the
                  pathname. */}
              <WizardStepContext.Provider value={stepValue}>
                <AnimatePresence mode="wait" initial={false}>
                  <WizardStep key={stepKey} vm={vm} onBackToSelect={onBackToSelect} />
                </AnimatePresence>
              </WizardStepContext.Provider>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/**
 * DIVERGENCE 1, in one place: the Design's `<Outlet/>`.
 *
 * The Design nests its five steps as five child routes and lets the router pick
 * one. The backend keeps the step in `useUploadOverlay` state (see the header for
 * why), so the choice is a switch instead of a route match. Everything else about
 * the swap is the Design's: this is rendered INSIDE <AnimatePresence mode="wait">
 * keyed on `stepKey`, exactly where the Outlet was, so one step's fade-out still
 * completes before the next fades in, inside a frame that never moves.
 *
 * THE MAPPING. Five drawn pages, three backend stages plus a phase:
 *
 *   P1 Select   — rendered by UploadOverlayPages, in an identical frame, BEFORE this
 *                 wizard mounts (see the header: the hook cannot exist without a
 *                 flow). `onBackToSelect` returns there from P2.
 *   P2 Describe — stage 'details'
 *   P3 Detail   — stage 'instructions'
 *   P4 Review   — stage 'review'
 *   P5 Publish  — phase.kind === 'done'
 *
 * FOUR PHASES THE DESIGN DOES NOT DRAW: 'loading', 'unavailable', 'resume' and
 * 'duplicates'. They are real states of this flow with no frame to copy, so they
 * are NOT invented here — the caller (UploadOverlayReskin) keeps handling them
 * before it mounts this wizard, and this switch only ever sees the drawn ones.
 */
function WizardStep({ vm, onBackToSelect }: { vm: UploadOverlayVm; onBackToSelect: () => void }) {
  if (vm.phase.kind === 'done') return <UploadPublishPage vm={vm} />;

  switch (vm.stage) {
    case 'details':
      // P2's Back goes to P1 — the previous frame, not a dismiss.
      return <UploadDescribePage vm={vm} onBack={onBackToSelect} />;
    case 'instructions':
      return <UploadDetailPage vm={vm} />;
    case 'review':
      return <UploadReviewPage vm={vm} />;
  }
}
